<template>
  <div v-show="isCustomFieldValid" class="tw-relative">
    <label
      v-if="!props.flat && !props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>

      <button
        v-if="showClear"
        tabindex="-1"
        class="primary-link tw-ml-1 tw-normal-case"
        @click="emit('value-updated', null)"
        @keyup.enter="emit('value-updated', null)"
      >
        Clear
      </button>
    </label>

    <ds-select
      :key="`select-${customField.id}-${isMultiSelect}`"
      ref="selectEl"
      :model-value="selectedOptions"
      :options="optionsComputed"
      :multiple="isMultiSelect"
      :readonly="props.readonly"
      :hide-dropdown-icon="props.readonly"
      :outlined="true"
      :should-auto-focus="props.shouldAutoFocus"
      :class="{
        'form-input--error': errorMessage && showErrorMessage,
        'choice-field--borderless': props.borderless,
      }"
      class="custom-field-select__select tw-bg-white"
      popup-content-class="custom-field-select__popup-content"
      @update:model-value="onValueChange"
      @clear="emit('value-updated', null)"
      @keyup.stop.escape="model = props.value || null"
      @keyup.escape="emit('custom-field-escape')"
      @keyup.delete="handleDeleteKey"
      @popup-show="emit('popup-show')"
      @popup-hide="emitChange"
    >
      <template #selected>
        <span
          v-if="!selectedOptions.length"
          class="trigger-container tw-select-none tw-text-gray-medium"
        >
          <template v-if="props.readonly">Not set</template>
          <template v-else>
            Select {{ isMultiSelect ? 'options' : 'an option' }}
          </template>
        </span>
        <div
          class="trigger-container tw-flex tw-w-full tw-max-w-full tw-items-center"
        >
          <span class="tw-truncate tw-text-xs">
            {{ selectedOptions.map((m) => m.label).join(', ') }}
          </span>

          <button
            v-if="showClear && props.hideLabel"
            tabindex="-1"
            class="primary-link tw-ml-auto tw-normal-case"
            @click.stop.prevent="emit('value-updated', null)"
            @keyup.enter="emit('value-updated', null)"
          >
            Clear
          </button>
        </div>
      </template>
    </ds-select>
    <span
      v-if="!!errorMessage && showErrorMessage && !props.flat"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { QSelect } from 'quasar';
import { ref, computed, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import { CustomField, CustomFieldOption, CustomFieldType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string[];
  clearable?: boolean;
  flat?: boolean;
  hideLabel?: boolean;
  borderless?: boolean;
  readonly?: boolean;
  shouldAutoFocus?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string[] | null): void;
  (e: 'custom-field-escape'): void;
  (e: 'popup-show'): void;
  (e: 'popup-hide'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const selectEl = ref<InstanceType<typeof QSelect> | null>(null);

const showClear = computed(() => {
  return (
    !!model.value?.length &&
    props.clearable &&
    !props.customField.required &&
    !props.readonly
  );
});

const optionsComputed = computed(() => {
  return [...(props.customField.options || [])];
});

const isMultiSelect = computed(() => {
  return props.customField.isMultiSelect;
});

const isCustomFieldValid = computed(() => {
  if (props.customField.type !== CustomFieldType.CHOICE) return false;
  return true;
});

const model = ref<string[] | null>(props.value || null);
watch(
  () => props.value,
  () => {
    model.value = props.value || null;
  }
);
watch(isMultiSelect, () => {
  model.value = props.value || null;
});

const selectedOptions = computed(() => {
  if (!model.value) return [];
  return optionsComputed.value.filter((o) => model.value?.includes(o.id));
});

const errorMessage = computed(() => {
  if (!props.customField.required) return '';
  if (!model.value) {
    return 'This field is required';
  }
  return '';
});

const showErrorMessage = ref(false);

watch(
  model,
  () => {
    showErrorMessage.value = false;
  },
  { deep: true }
);

function onValueChange(val: CustomFieldOption | CustomFieldOption[] | null) {
  if (!val) {
    model.value = null;
  } else {
    if (!Array.isArray(val)) val = [val];
    model.value = val.map((v) => v.id);
  }

  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
}

const hasChanges = computed(() => {
  return JSON.stringify(model.value) !== JSON.stringify(props.value);
});

function emitChange() {
  emit('popup-hide');

  if (
    props.customField.required &&
    props.customField.isMultiSelect &&
    Array.isArray(model.value) &&
    !model.value.length
  ) {
    model.value = props.value || null;
    return;
  }

  if (hasChanges.value) {
    emit('value-updated', model.value);
  }
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

function handleDeleteKey() {
  if (props.clearable && !props.customField.required) {
    emit('value-updated', null);
  }
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>

<style lang="scss" scoped>
.choice-field--borderless {
  :deep(.q-field__control:after),
  :deep(.q-field__control:before) {
    display: none;
  }
}
</style>

<style lang="scss">
.custom-field-select__popup-content {
  .q-item--active {
    @apply tw-bg-primary tw-bg-opacity-10;
  }
}
</style>
